// The snippet ships as a standalone CDN bundle, so – like packages/mailer and
// packages/auth – it reads no Paraglide runtime. Its strings come from
// `messages.generated.ts`, which `vp run l10n:bundle` copies out of
// messages/*.json. The strings stay in messages/*.json, so Tolgee and the
// drift gate still own them.
//
// Only `SUPPORTED_LOCALES` (en/fr) ship inline – `widget.js` is fetched on
// every host-page load under a hard, CI-enforced gzip ceiling
// (`apps/snippet/scripts/check-size.ts`), and all 17 widget locales blew
// that budget by nearly 9 KB. A visitor in another language sees this
// bundled chrome in English or French for the brief window before the
// widget iframe loads and posts its own Paraglide-rendered strings back
// through `setHostStrings`, which is not locale-limited and overwrites
// whatever this file rendered.
import { MESSAGES, STATUS_LABELS } from "./messages.generated";

type MessageKey = keyof (typeof MESSAGES)[typeof BASE_LOCALE];

export type Locale = keyof typeof MESSAGES;

const BASE_LOCALE = "en" satisfies Locale;

// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- the keys of a const object are its locales
const LOCALES = Object.keys(MESSAGES) as readonly Locale[];

const isLocale = (value: string): value is Locale =>
	Object.hasOwn(MESSAGES, value);

// Strings the widget frame handed over in its own language. The frame knows
// the signed-in user's preference, which the host page cannot; they win over
// the bundled catalog until the frame sends a new set, and a key the frame
// did not send keeps the bundled wording.
let hostLabels: Readonly<Partial<Record<MessageKey, string>>> = {};
let hostStatuses: Readonly<Record<string, string>> = {};
let hostStatusHints: Readonly<Record<string, string>> = {};
let hostTimeframes: Readonly<Record<string, string>> = {};
// The language those strings are written in, once the frame sent a set.
let hostLanguage: string | undefined = undefined;

// A BCP 47 tag: 2–3 letter language, dash-separated 2–8 character subtags.
const LANGUAGE_TAG = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/u;
const MAX_TAG_LENGTH = 35;

// The first candidate that is a well-formed language tag, canonicalized, or
// undefined. Keeps the full tag – the widget negotiates it against the
// languages it can render. Exported for tests; `resolveFrameLocaleHint` reads
// the host page.
export const pickFrameLocaleHint = (
	candidates: readonly (string | undefined)[],
): string | undefined => {
	for (const candidate of candidates) {
		const trimmed = candidate?.trim() ?? "";
		if (
			trimmed === "" ||
			trimmed.length > MAX_TAG_LENGTH ||
			!LANGUAGE_TAG.test(trimmed)
		) {
			continue;
		}
		try {
			return Intl.getCanonicalLocales(trimmed)[0];
		} catch {
			// Not a real tag – try the next hint.
		}
	}
	return undefined;
};

const isStringMap = (value: unknown): value is Record<string, string> =>
	typeof value === "object" &&
	value !== null &&
	!Array.isArray(value) &&
	Object.values(value).every((entry) => typeof entry === "string");

/**
 * Accepts the `strings` a `weloop:locale` message carries, and the language
 * tag they are written in. Returns whether anything changed, so the launcher
 * can redraw its labels. Malformed input is ignored – the bundled catalog
 * keeps rendering.
 */
export const setHostStrings = (raw: unknown, language?: unknown): boolean => {
	if (typeof raw !== "object" || raw === null) {
		return false;
	}
	const { labels, statuses, statusHints, timeframes } = raw as {
		readonly labels?: unknown;
		readonly statuses?: unknown;
		readonly statusHints?: unknown;
		readonly timeframes?: unknown;
	};
	if (!isStringMap(labels) || !isStringMap(statuses)) {
		return false;
	}
	// Older frames send neither map – the bundled catalog keeps those words.
	const nextStatusHints = isStringMap(statusHints) ? statusHints : {};
	const nextTimeframes = isStringMap(timeframes) ? timeframes : {};
	const known = Object.keys(MESSAGES[BASE_LOCALE]);
	const nextLabels: Partial<Record<MessageKey, string>> = {};
	for (const key of known) {
		const value = labels[key];
		if (value !== undefined) {
			// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- `known` is the catalog's own key list
			nextLabels[key as MessageKey] = value;
		}
	}
	const changed =
		JSON.stringify(nextLabels) !== JSON.stringify(hostLabels) ||
		JSON.stringify(statuses) !== JSON.stringify(hostStatuses) ||
		JSON.stringify(nextStatusHints) !== JSON.stringify(hostStatusHints) ||
		JSON.stringify(nextTimeframes) !== JSON.stringify(hostTimeframes);
	hostLabels = nextLabels;
	hostStatuses = statuses;
	hostStatusHints = nextStatusHints;
	hostTimeframes = nextTimeframes;
	hostLanguage = pickFrameLocaleHint([
		typeof language === "string" ? language : undefined,
	]);
	return changed;
};

// The catalogues' only right-to-left language today is Arabic; the others
// cover tags a host page or a newer frame may still send.
const RTL_LANGUAGE = /^(?:ar|fa|he|ur)\b/iu;

/**
 * Mark a surface's host element with the language its words are in, so
 * screen readers pronounce them right and `text-align: start` follows the
 * script. The frame's strings win over the bundled locale, like `t`.
 */
// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
export const setLanguage = (host: HTMLElement, locale: Locale): void => {
	const language = hostLanguage ?? locale;
	host.lang = language;
	host.dir = RTL_LANGUAGE.test(language) ? "rtl" : "ltr";
};

export const t = (locale: Locale, key: MessageKey): string =>
	hostLabels[key] ?? MESSAGES[locale][key];

// A status the catalog has no label for (a newer server than this bundle)
// falls back to the raw value – wrong-language beats wrong-information.
export const statusLabel = (locale: Locale, status: string): string => {
	const labels: Readonly<Record<string, string>> = STATUS_LABELS[locale];
	return hostStatuses[status] ?? labels[status] ?? status;
};

// The two maps below are not bundled: `widget.js` sits at its gzip ceiling,
// and the frame posts them with its other strings as soon as it loads, before
// any unread message can reach the card. Until then – or on a value this
// frame did not send – they are undefined, and the card falls back to the
// wording without them rather than showing a raw identifier.

/**
 * The plain-words line under a status change – what the status means for
 * the person who asked.
 */
export const statusHint = (status: string): string | undefined =>
	hostStatusHints[status];

/** The label of an expected timeframe. */
export const timeframeLabel = (timeframe: string): string | undefined =>
	hostTimeframes[timeframe];

// The snippet reads raw catalog strings (no paraglide runtime), so `{param}`
// placeholders are substituted with this minimal helper. Keep snippet keys to
// simple placeholders – no ICU plurals here.
export const tWith = (
	locale: Locale,
	key: MessageKey,
	params: Readonly<Record<string, string>>,
): string => {
	let text = t(locale, key);
	for (const [name, value] of Object.entries(params)) {
		text = text.replaceAll(`{${name}}`, value);
	}
	return text;
};

// Map a raw language tag down to a locale this bundle carries inline:
// "fr-FR" -> "fr", "fr-CA" -> "fr". An exact tag wins; otherwise the first
// carried locale sharing the language – a no-op today since `LOCALES` is
// `SUPPORTED_LOCALES` (en/fr, each its own language), kept general so a
// future locale with regional variants (a `pt-BR`/`pt-PT` pair, say) narrows
// the same way `resolveWidgetUiLocale` does for the frame. Returns undefined
// for empty or unsupported tags so the caller can fall through to the next
// candidate.
export const toSupportedLocale = (
	raw: string | undefined,
): Locale | undefined => {
	const tag = pickFrameLocaleHint([raw]);
	if (tag === undefined) {
		return undefined;
	}
	if (isLocale(tag)) {
		return tag;
	}
	const language = tag.toLowerCase().split("-")[0] ?? "";
	return LOCALES.find(
		(locale) => locale.toLowerCase().split("-")[0] === language,
	);
};

// The snippet renders the FAB on the host page before the widget iframe loads,
// so it has no Weloop-controlled locale signal – no SSR `<html lang>`, no
// cookie, no widget. Resolve from host-page hints instead, in order: an
// explicit per-project override, the host page's `<html lang>`, the browser's
// preferred language, then the base locale.
export const resolveLocale = (override?: string): Locale => {
	const candidates = [
		override,
		document.documentElement.lang,
		navigator.language,
	];
	for (const candidate of candidates) {
		const locale = toSupportedLocale(candidate);
		if (locale !== undefined) {
			return locale;
		}
	}
	return BASE_LOCALE;
};

// The language hint forwarded to the widget iframe as `?lang=`, so its server
// can render the first paint in the host's language before any session exists.
// Same candidate order as `resolveLocale`.
export const resolveFrameLocaleHint = (override?: string): string | undefined =>
	pickFrameLocaleHint([
		override,
		document.documentElement.lang,
		navigator.language,
	]);
